// Copyright (C) 2011 by Will Tomlins
//
// Github profile: http://github.com/layam
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { MILLISECONDS_IN_YEAR } from 'constants/DateConstants';

enum Tense {
  Past = 'past',
  Future = 'future'
}

enum DateFormatKey {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Months = 'months',
  Years = 'years'
}

type RelativeDateFormat = {
  ceiling: number;
  text: string;
  key: DateFormatKey;
};

type DateFormats = {
  START_TRANSLATE: 'START_TRANSLATE';
  past: RelativeDateFormat[];
  future: RelativeDateFormat[];
  time_units: [number, DateFormatKey][];
  END_TRANSLATE: 'END_TRANSLATE';
};

function humanized_time_span(timestamp: string, languageRegionCode?: LanguageRegionCode, ref_date?: Date) {
  // Date Formats must be be ordered smallest -> largest and must end in a format with ceiling of null
  const date_formats: DateFormats = {
    START_TRANSLATE: 'START_TRANSLATE',
    past: [
      { ceiling: 60, text: '{breakdown} seconds ago', key: DateFormatKey.Seconds },
      { ceiling: 3600, text: '{breakdown} minutes ago', key: DateFormatKey.Minutes },
      { ceiling: 86400, text: '{breakdown} hours ago', key: DateFormatKey.Hours },
      { ceiling: 2629744, text: '{breakdown} days ago', key: DateFormatKey.Days },
      { ceiling: 31556926, text: '{breakdown} months ago', key: DateFormatKey.Months },
      { ceiling: null, text: '{breakdown} years ago', key: DateFormatKey.Years }
    ],
    future: [
      { ceiling: 60, text: 'in {breakdown} seconds', key: DateFormatKey.Seconds },
      { ceiling: 3600, text: 'in {breakdown} minutes', key: DateFormatKey.Minutes },
      { ceiling: 86400, text: 'in {breakdown} hours', key: DateFormatKey.Hours },
      { ceiling: 2629744, text: 'in {breakdown} days', key: DateFormatKey.Days },
      { ceiling: 31556926, text: 'in {breakdown} months', key: DateFormatKey.Months },
      { ceiling: null, text: 'in {breakdown} years', key: DateFormatKey.Years }
    ],
    time_units: [
      // Time units must be be ordered largest -> smallest
      [31556926, DateFormatKey.Years],
      [2629744, DateFormatKey.Months],
      [86400, DateFormatKey.Days],
      [3600, DateFormatKey.Hours],
      [60, DateFormatKey.Minutes],
      [1, DateFormatKey.Seconds]
    ],
    END_TRANSLATE: 'END_TRANSLATE'
  };

  const date = new Date(timestamp);
  const locale = languageRegionCode || 'en-US';

  // if date is more than a year ago, format it as (Month, year)
  const now = new Date(Date.now());
  const diff = +now - +date;
  if (diff > MILLISECONDS_IN_YEAR) {
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  }

  ref_date = ref_date ? new Date(ref_date) : new Date(Date.now());
  let seconds_difference = (+ref_date - +date) / 1000;

  let tense: Tense = Tense.Past;
  if (seconds_difference < 0) {
    tense = Tense.Future;
    seconds_difference = 0 - seconds_difference;
  }

  function get_format(): RelativeDateFormat | null {
    for (let i = 0; i < date_formats[tense].length; i++) {
      if (date_formats[tense][i].ceiling == null || seconds_difference <= date_formats[tense][i].ceiling) {
        return date_formats[tense][i];
      }
    }
    return null;
  }

  function get_time_breakdown(): Record<DateFormatKey, number> {
    let seconds = seconds_difference;
    const breakdown = {} as Record<DateFormatKey, number>;
    for (let i = 0; i < date_formats.time_units.length; i++) {
      const occurences_of_unit = Math.floor(seconds / date_formats.time_units[i][0]);
      seconds -= date_formats.time_units[i][0] * occurences_of_unit;
      breakdown[date_formats.time_units[i][1]] = occurences_of_unit;
    }
    return breakdown;
  }

  function supplant(str: string, o: { breakdown: number }) {
    return str.replace(/{([^{}]*)}/g, (a: string, b: 'breakdown'): string => {
      const r = o[b];
      return typeof r === 'string' || typeof r === 'number' ? r.toString() : a;
    });
  }

  function render_date(date_format: RelativeDateFormat | null) {
    const breakdown = get_time_breakdown();
    const time_ago_text = supplant(date_format.text, {
      breakdown: breakdown[arguments[0].key as DateFormatKey]
    });
    return depluralize_time_ago_text(time_ago_text, breakdown);
  }

  function depluralize_time_ago_text(time_ago_text: string, breakdown: Record<DateFormatKey, number>): string {
    for (const i in breakdown) {
      const key = i as DateFormatKey;
      if (breakdown[key] == 1) {
        const regexp = new RegExp(`\\b${i}\\b`);
        time_ago_text = time_ago_text.replace(regexp, function () {
          return arguments[0].replace(/s\b/g, '');
        });
      }
    }
    return time_ago_text;
  }

  return render_date(get_format());
}

export { humanized_time_span };
